import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { rcTiers, rcDistributors, rcAcademy, rcSetTier, rcCreateTrack, rcListTracks, rcUpdateTrack, rcDeleteTrack, rcNowPlaying, uploadFile, resolveFileUrl, rcAgentSessions, rcAgentHistory, rcAgentSend, rcAgentDelete, academyCurriculum, academyLesson, academyComplete, academyMe, academyDownloadCertificate, specialistChat, specialistSessions, specialistHistory, specialistDeleteSession, researchCreate, cleanAiText } from '../lib/api';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import AgencyActions from '../components/AgencyActions';
import { toast } from 'sonner';
import { Radio, Music, UploadCloud, Library, Sparkles, GraduationCap, Link2, ExternalLink, Loader2, Play, Pause, Trash2, Check, Crown, Flame, Headphones, Clock, TrendingUp, Bot, Send, Plus, MessageSquare, Lock, Award, Star, ChevronRight, ArrowLeft, Download, Shield, Rocket, Coins, BookOpen, Mic, Building, Gem, Bookmark, X, Film, ListChecks } from 'lucide-react';

export default function ReaperCast() {
  const { user, refreshMe } = useApp();
  const tier = user?.reapercast_tier || 'free';

  return (
    <div data-testid="reapercast-page" className="max-w-[1240px] mx-auto">
      <header className="mb-8">
        <div className="flex items-center gap-2 text-[#a78bfa]"><Radio className="w-5 h-5" /><span className="uppercase text-[11px] tracking-widest font-bold">ReaperCast Streaming</span></div>
        <h1 className="font-display text-[36px] md:text-[54px] mt-2 leading-[1.02]">Listen, broadcast, and <span className="brand-gradient">distribute</span>.</h1>
        <p className="text-white/65 text-[15px] mt-3 max-w-[760px]">24/7 live indie radio, your artist studio, AI marketing agent, and partner distribution — all in one music-first surface. <Link to="/academy" className="text-[#ffcf52] underline">Looking for the Academy?</Link></p>
      </header>

      <Tabs defaultValue="radio" className="mt-6">
        <TabsList className="bg-white/[0.04] border border-white/10 flex-wrap h-auto gap-1 p-1">
          <TabsTrigger value="radio" data-testid="rc-tab-radio"><Headphones className="w-4 h-4 mr-1.5" /> Live Radio</TabsTrigger>
          <TabsTrigger value="studio" data-testid="rc-tab-studio"><UploadCloud className="w-4 h-4 mr-1.5" /> My Studio</TabsTrigger>
          <TabsTrigger value="agent" data-testid="rc-tab-agent"><Bot className="w-4 h-4 mr-1.5" /> AI Marketing Agent</TabsTrigger>
          <TabsTrigger value="distribute" data-testid="rc-tab-distribute"><ExternalLink className="w-4 h-4 mr-1.5" /> Distribution</TabsTrigger>
        </TabsList>

        <TabsContent value="radio" className="mt-6"><RadioTab /></TabsContent>
        <TabsContent value="studio" className="mt-6"><StudioTab tier={tier} onRefresh={refreshMe} /></TabsContent>
        <TabsContent value="agent" className="mt-6"><AgentTab /></TabsContent>
        <TabsContent value="distribute" className="mt-6"><DistributeTab /></TabsContent>
      </Tabs>
    </div>
  );
}

// ===== Radio =====
function RadioTab() {
  const [np, setNp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [playing, setPlaying] = useState(false);
  const audioRef = useRef(null);

  const load = async () => { try { setNp(await rcNowPlaying()); } catch {} finally { setLoading(false); } };
  useEffect(() => { load(); const t = setInterval(load, 15000); return () => clearInterval(t); }, []);
  useEffect(() => {
    if (!audioRef.current || !np?.now) return;
    const el = audioRef.current;
    const url = resolveFileUrl(np.now.file_url);
    if (el.dataset.currentId !== np.now.id) {
      el.dataset.currentId = np.now.id;
      el.src = url;
      el.addEventListener('loadedmetadata', () => { try { el.currentTime = np.now.offset_s || 0; } catch {} }, { once: true });
      if (playing) el.play().catch(() => {});
    }
  }, [np, playing]);

  const toggle = async () => {
    if (!audioRef.current) return;
    if (playing) { audioRef.current.pause(); setPlaying(false); }
    else { try { await audioRef.current.play(); setPlaying(true); } catch { toast.error('Unable to play'); } }
  };

  if (loading) return <div className="flex items-center gap-2 text-white/60 py-10"><Loader2 className="w-5 h-5 animate-spin" /> Tuning in…</div>;

  if (np?.empty) return (
    <div className="card-shell p-10 text-center" data-testid="radio-empty">
      <Radio className="w-10 h-10 mx-auto text-white/25" />
      <h3 className="font-display text-[22px] mt-3">Radio silence (for now)</h3>
      <p className="text-white/55 text-[13.5px] mt-1">Be the first. Upload a track in Studio and it goes into rotation instantly.</p>
    </div>
  );

  const n = np.now;
  return (
    <div className="card-shell overflow-hidden" data-testid="radio-player">
      <div className="grid md:grid-cols-[420px_1fr] gap-0">
        <div className="relative aspect-square bg-black">
          <img src={resolveFileUrl(n.cover_url) || 'https://images.unsplash.com/photo-1511671782779-c97d3d27a1d4?w=800'} alt={n.title} className="w-full h-full object-cover" />
          <div className="absolute inset-0" style={{ background: 'linear-gradient(0deg, rgba(0,0,0,0.75), rgba(0,0,0,0.1))' }} />
          <div className="absolute top-3 left-3 flex items-center gap-1.5 text-[11px] uppercase tracking-wider bg-black/70 border border-white/10 rounded-full px-2.5 py-1">
            <span className="w-1.5 h-1.5 rounded-full bg-[#ff2e63] animate-pulse" /> LIVE
          </div>
          <button onClick={toggle} className="absolute bottom-4 right-4 w-14 h-14 rounded-full grid place-items-center shadow-2xl transition hover:scale-105" style={{ background: 'linear-gradient(135deg,#ff2e63,#ff7a00)' }} data-testid="radio-play-toggle">
            {playing ? <Pause className="w-6 h-6 text-white" /> : <Play className="w-6 h-6 text-white" style={{ marginLeft: 2 }} />}
          </button>
        </div>
        <div className="p-6">
          <div className="text-[12px] uppercase tracking-wider text-white/45">Now playing on ReaperCast Radio</div>
          <h3 className="font-display text-[28px] md:text-[38px] mt-2 leading-tight">{n.title}</h3>
          <div className="text-[14.5px] text-white/75 mt-1">{n.artist_name}</div>
          <div className="flex flex-wrap items-center gap-2 mt-3 text-[12px] text-white/50">
            {n.genre && <span className="chip">{n.genre}</span>}
            {n.bpm && <span className="chip">{n.bpm} BPM</span>}
            {n.key && <span className="chip">Key {n.key}</span>}
            <span className="chip flex items-center gap-1"><Clock className="w-3 h-3" /> {Math.floor(n.duration_s/60)}:{String(n.duration_s%60).padStart(2,'0')}</span>
          </div>
          <div className="mt-6">
            <div className="text-[11px] uppercase tracking-wider text-white/45 mb-2">Up next</div>
            <ul className="space-y-2">
              {(np.next || []).map(t => (
                <li key={t.id} className="flex items-center gap-3 text-[13.5px]">
                  <img src={resolveFileUrl(t.cover_url) || 'https://images.unsplash.com/photo-1511671782779-c97d3d27a1d4?w=200'} alt="" className="w-10 h-10 rounded object-cover" />
                  <div className="min-w-0 flex-1"><div className="font-semibold truncate">{t.title}</div><div className="text-white/50 text-[12px] truncate">{t.artist_name}</div></div>
                </li>
              ))}
            </ul>
          </div>
          <audio ref={audioRef} preload="metadata" onEnded={() => { setPlaying(false); load(); }} />
        </div>
      </div>
    </div>
  );
}

// ===== Studio =====
function StudioTab({ tier }) {
  const [tracks, setTracks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [myTier, setMyTier] = useState(null);
  const [showForm, setShowForm] = useState(false);

  useEffect(() => { (async () => {
    try {
      const [mine, tiers] = await Promise.all([rcListTracks({ mine: true }), rcTiers()]);
      setTracks(mine); setMyTier(tiers.find(t => t.id === tier));
    } catch { toast.error('Failed to load'); } finally { setLoading(false); }
  })(); }, [tier]);

  const doDelete = async (t) => {
    if (!window.confirm(`Delete "${t.title}"?`)) return;
    try { await rcDeleteTrack(t.id); setTracks(list => list.filter(x => x.id !== t.id)); toast.success('Deleted'); }
    catch { toast.error('Failed'); }
  };

  if (loading) return <div className="flex items-center gap-2 text-white/60 py-10"><Loader2 className="w-5 h-5 animate-spin" /> Loading…</div>;

  const limitText = myTier?.track_limit === null ? 'Unlimited' : `${tracks.length} / ${myTier?.track_limit}`;
  const atLimit = myTier?.track_limit !== null && tracks.length >= (myTier?.track_limit || 0);

  return (
    <div data-testid="rc-studio">
      <div className="card-shell p-5 flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="text-[12px] uppercase tracking-wider text-white/45">Your catalog</div>
          <div className="font-display text-[26px]">{limitText}</div>
          <div className="text-[12.5px] text-white/55">On {myTier?.name || 'ReaperCast Free'}</div>
        </div>
        <button onClick={() => setShowForm(true)} disabled={atLimit} className="btn-primary disabled:opacity-50" data-testid="rc-new-track-btn">
          <UploadCloud className="w-4 h-4" /> Upload track
        </button>
      </div>

      {atLimit && <div className="card-shell p-4 mt-3 text-[13px] text-[#ffcf52] border-[#ffcf52]/40 bg-[#ffcf52]/10">You&apos;ve hit your {myTier.name} track limit. Upgrade your ReaperCast plan to add more.</div>}

      {showForm && <UploadTrackForm onClose={() => setShowForm(false)} onCreated={t => { setTracks(l => [t, ...l]); setShowForm(false); }} />}

      <div className="mt-6">
        {tracks.length === 0 ? (
          <div className="card-shell p-10 text-center">
            <Library className="w-10 h-10 mx-auto text-white/25" />
            <div className="font-display text-[20px] mt-3">No tracks yet</div>
            <div className="text-[13px] text-white/55 mt-1">Upload your first track — it goes into the 24/7 Radio rotation the moment it lands.</div>
          </div>
        ) : (
          <div className="card-shell overflow-hidden">
            <table className="w-full text-[13.5px]">
              <thead className="bg-white/[0.03] text-white/55">
                <tr>
                  <th className="text-left px-4 py-2">Track</th>
                  <th className="text-left px-3 py-2">Genre</th>
                  <th className="text-left px-3 py-2">ISRC</th>
                  <th className="text-center px-3 py-2">In rotation</th>
                  <th className="text-right px-3 py-2">Plays</th>
                  <th className="px-4 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {tracks.map(t => (
                  <tr key={t.id} className="border-t border-white/5" data-testid={`track-row-${t.id}`}>
                    <td className="px-4 py-3">
                      <div className="font-semibold">{t.title}</div>
                      <div className="text-[12px] text-white/55">{t.artist_name} · {Math.floor(t.duration_s/60)}:{String(t.duration_s%60).padStart(2,'0')}</div>
                    </td>
                    <td className="px-3 py-3 text-white/75">{t.genre || '—'}</td>
                    <td className="px-3 py-3 font-mono text-[12px] text-white/60">{t.isrc || '—'}</td>
                    <td className="px-3 py-3 text-center">{t.in_rotation ? <Check className="w-4 h-4 text-[#22d3ee] inline" /> : <span className="text-white/35">—</span>}</td>
                    <td className="px-3 py-3 text-right">{t.plays || 0}</td>
                    <td className="px-4 py-3 text-right">
                      <button onClick={() => doDelete(t)} className="text-white/60 hover:text-[#ff6b8a]" aria-label="Delete"><Trash2 className="w-4 h-4" /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function UploadTrackForm({ onClose, onCreated }) {
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState('');
  const [genre, setGenre] = useState('');
  const [isrc, setIsrc] = useState('');
  const [bpm, setBpm] = useState('');
  const [musicKey, setMusicKey] = useState('');
  const [rights, setRights] = useState(false);
  const [progress, setProgress] = useState(0);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!file) return toast.error('Pick an audio file');
    if (!title.trim()) return toast.error('Title required');
    if (!rights) return toast.error('Confirm you own the rights');
    setBusy(true);
    try {
      const up = await uploadFile(file, setProgress);
      // Compute duration client-side via audio metadata
      const duration = await new Promise(res => {
        const a = new Audio(); a.preload = 'metadata';
        a.onloadedmetadata = () => res(Math.round(a.duration || 180));
        a.onerror = () => res(180);
        a.src = up.url.startsWith('http') ? up.url : (process.env.REACT_APP_BACKEND_URL + up.url);
      });
      const created = await rcCreateTrack({
        title: title.trim(),
        genre: genre.trim() || undefined,
        isrc: isrc.trim() || undefined,
        bpm: bpm ? parseInt(bpm) : undefined,
        key: musicKey.trim() || undefined,
        file_url: up.url,
        duration_s: duration,
        rights_confirmed: true,
      });
      toast.success('Track uploaded');
      onCreated(created);
    } catch (err) {
      if (err?.response?.status === 402) toast.error(err.response.data?.detail?.message || 'Plan limit reached');
      else toast.error(err?.response?.data?.detail || 'Upload failed');
    } finally { setBusy(false); setProgress(0); }
  };

  return (
    <form onSubmit={submit} className="card-shell p-5 mt-4 space-y-4" data-testid="rc-upload-form">
      <label className="block border-2 border-dashed border-white/15 rounded-xl p-5 text-center hover:border-[#ff2e63]/60 cursor-pointer">
        <input type="file" accept="audio/*" className="hidden" onChange={e => { const f = e.target.files?.[0]; if (!f) return; setFile(f); if (!title) setTitle(f.name.replace(/\.[^.]+$/, '')); }} data-testid="rc-track-file-input" />
        <Music className="w-8 h-8 mx-auto text-white/50" />
        <div className="mt-2 font-semibold">{file ? file.name : 'Drop or click to pick an audio file'}</div>
        <div className="text-[12px] text-white/50 mt-1">MP3, WAV, FLAC · stored against your ReaperCast quota</div>
      </label>
      {busy && <div><div className="h-2 bg-white/10 rounded-full overflow-hidden"><div className="h-full" style={{ width: progress + '%', background: 'linear-gradient(90deg,#ff2e63,#22d3ee)', transition: 'width 0.2s linear' }} /></div><div className="text-[12px] text-white/60 mt-1">Uploading… {Math.round(progress)}%</div></div>}
      <div className="grid sm:grid-cols-2 gap-3">
        <input value={title} onChange={e => setTitle(e.target.value)} placeholder="Track title" className="input-field" data-testid="rc-track-title" />
        <input value={genre} onChange={e => setGenre(e.target.value)} placeholder="Genre (lofi, hip-hop, indie…)" className="input-field" />
        <input value={isrc} onChange={e => setIsrc(e.target.value)} placeholder="ISRC (optional)" className="input-field font-mono" />
        <div className="grid grid-cols-2 gap-3">
          <input value={bpm} onChange={e => setBpm(e.target.value)} type="number" placeholder="BPM" className="input-field" />
          <input value={musicKey} onChange={e => setMusicKey(e.target.value)} placeholder="Key (Am, F#, C)" className="input-field" />
        </div>
      </div>
      <label className="flex items-start gap-2 card-shell p-3 cursor-pointer">
        <input type="checkbox" checked={rights} onChange={e => setRights(e.target.checked)} className="mt-0.5" data-testid="rc-rights-checkbox" />
        <span className="text-[13px] text-white/80">I own or have the rights to distribute this recording. VidCreForge / ReaperCast removes infringing content on DMCA notice.</span>
      </label>
      <div className="flex justify-end gap-2">
        <button type="button" onClick={onClose} className="btn-ghost">Cancel</button>
        <button disabled={busy} className="btn-primary disabled:opacity-50" data-testid="rc-track-submit">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Flame className="w-4 h-4" />} Publish to catalog</button>
      </div>
    </form>
  );
}

// ===== Distribute (affiliate links) =====
function DistributeTab() {
  const [partners, setPartners] = useState([]);
  useEffect(() => { rcDistributors().then(setPartners).catch(() => {}); }, []);
  return (
    <div data-testid="rc-distribute-tab">
      <div className="card-shell p-5" style={{ background: 'linear-gradient(90deg, rgba(255,46,99,0.08), rgba(167,139,250,0.05))', borderColor: 'rgba(255,46,99,0.35)' }}>
        <div className="flex items-center gap-2 text-[#ff6b8a]"><Sparkles className="w-4 h-4" /><span className="uppercase text-[11px] tracking-widest font-bold">Until we launch our own distributor</span></div>
        <h3 className="font-display text-[22px] mt-1">Get your music on Spotify, Apple Music, TikTok &amp; 150+ DSPs</h3>
        <p className="text-white/65 text-[13px] mt-2 max-w-[700px]">We partner with leading distributors to get your tracks worldwide in 24h. Use any of these through our referral links — pricing identical to going direct, and you help keep ReaperCast alive.</p>
      </div>
      <div className="grid md:grid-cols-2 gap-4 mt-6">
        {partners.map(p => (
          <a key={p.id} href={p.url} target="_blank" rel="noreferrer sponsored" className="card-shell p-5 hover-lift block" data-testid={`distributor-${p.id}`}>
            <div className="flex items-center justify-between">
              <div className="font-display text-[22px]">{p.name}</div>
              <div className="text-[13px] text-[#22d3ee] font-semibold">{p.price}</div>
            </div>
            <div className="grid grid-cols-2 gap-3 mt-3 text-[13px]">
              <div>
                <div className="text-[11px] uppercase tracking-wider text-[#22d3ee]/80 font-semibold mb-1">Strengths</div>
                <ul className="space-y-1">{p.pros.map((x, i) => <li key={i} className="flex items-start gap-1.5 text-white/80"><Check className="w-3.5 h-3.5 text-[#22d3ee] mt-0.5 shrink-0" />{x}</li>)}</ul>
              </div>
              <div>
                <div className="text-[11px] uppercase tracking-wider text-white/45 font-semibold mb-1">Trade-offs</div>
                <ul className="space-y-1">{p.cons.map((x, i) => <li key={i} className="text-white/55">{x}</li>)}</ul>
              </div>
            </div>
            <div className="mt-4 text-[13px] text-[#ff6b8a] flex items-center gap-1">Open {p.name} <ExternalLink className="w-3.5 h-3.5" /></div>
          </a>
        ))}
      </div>
    </div>
  );
}

// ===== Shared: AI message bubble with Save-to-Research =====
function AssistantBubble({ message, avatar, sourceKey }) {
  const text = cleanAiText(message.text);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const title = text.split('\n')[0].slice(0, 120) || 'AI response';
      await researchCreate({
        title,
        body: text,
        source: sourceKey,
        source_session_id: message.session_id || null,
        source_message_id: message.id,
        kind: 'content',
      });
      setSaved(true);
      toast.success('Saved to Research');
    } catch { toast.error('Save failed'); }
    finally { setSaving(false); }
  };

  const copy = async () => {
    try { await navigator.clipboard.writeText(text); toast.success('Copied'); } catch {}
  };

  return (
    <div className="flex gap-3 group">
      {avatar}
      <div className="max-w-[78%]">
        <div className="rounded-2xl px-4 py-2.5 text-[14px] leading-relaxed whitespace-pre-wrap bg-white/[0.04] border border-white/10">{text}</div>
        <div className="mt-1.5 flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
          <button onClick={save} disabled={saving || saved} className="text-[11.5px] text-white/55 hover:text-[#22d3ee] flex items-center gap-1" data-testid="ai-bubble-save">
            {saved ? <><Check className="w-3 h-3" /> Saved</> : (saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <><Bookmark className="w-3 h-3" /> Save to Research</>)}
          </button>
          <button onClick={copy} className="text-[11.5px] text-white/55 hover:text-white/85 flex items-center gap-1" data-testid="ai-bubble-copy">
            Copy
          </button>
        </div>
      </div>
    </div>
  );
}

function UserBubble({ message }) {
  return (
    <div className="flex gap-3 justify-end">
      <div className="max-w-[78%] rounded-2xl px-4 py-2.5 text-[14px] leading-relaxed whitespace-pre-wrap bg-[#ff2e63]/20 border border-[#ff2e63]/30">{message.text}</div>
    </div>
  );
}

// ===== Academy (gamified, tier-gated, with quizzes + certificates) =====
const TRACK_ICONS = { Shield, Rocket, Coins, BookOpen, Mic, Building, Film };

export function AcademyTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTrack, setActiveTrack] = useState(null);
  const [activeLesson, setActiveLesson] = useState(null);

  const load = async () => { try { setData(await academyCurriculum()); } catch { toast.error('Failed to load academy'); } finally { setLoading(false); } };
  useEffect(() => { load(); }, []);

  if (loading) return <div className="flex items-center gap-2 text-white/60 py-10"><Loader2 className="w-5 h-5 animate-spin" /> Loading curriculum…</div>;
  if (!data) return null;

  // Lesson reader view
  if (activeTrack && activeLesson) {
    return <LessonReader trackId={activeTrack} lessonId={activeLesson} onBack={() => { setActiveLesson(null); load(); }} onCompleteTrack={() => { setActiveLesson(null); setActiveTrack(null); load(); }} />;
  }

  // Track detail view
  if (activeTrack) {
    const t = data.tracks.find(x => x.id === activeTrack);
    if (!t) return null;
    const Ic = TRACK_ICONS[t.icon] || GraduationCap;
    const completed = t.completed_lessons === t.total_lessons;
    return (
      <div data-testid="rc-academy-track-detail">
        <button onClick={() => setActiveTrack(null)} className="btn-ghost text-[13px] mb-4"><ArrowLeft className="w-4 h-4" /> Back to tracks</button>
        <div className="card-shell p-6 flex items-start gap-5" style={{ borderColor: t.color + '55' }}>
          <div className="w-14 h-14 rounded-xl grid place-items-center shrink-0" style={{ background: t.color + '22', color: t.color }}>
            <Ic className="w-7 h-7" />
          </div>
          <div className="flex-1">
            <div className="text-[11px] uppercase tracking-wider font-bold" style={{ color: t.color }}>{t.tier} tier · {t.completed_lessons}/{t.total_lessons} lessons</div>
            <div className="font-display text-[28px] mt-1">{t.title}</div>
            <p className="text-white/70 text-[14px] mt-2 leading-relaxed max-w-[680px]">{t.summary}</p>
            {completed && <CertificateActions trackId={t.id} />}
          </div>
        </div>

        <AgencyActions trackId={t.id} color={t.color} />

        <Link to={`/academy/action-center/${t.id}`} className="card-shell p-4 mt-3 flex items-center justify-between hover-lift" data-testid={`rc-academy-action-center-${t.id}`}>
          <div className="flex items-center gap-3 min-w-0">
            <span className="w-10 h-10 rounded-xl grid place-items-center shrink-0" style={{ background: t.color + '22', color: t.color }}>
              <ListChecks className="w-5 h-5" />
            </span>
            <div className="min-w-0">
              <div className="font-semibold text-[14px]">Open the Action Center</div>
              <div className="text-[12px] text-white/55">Checklist · tools · deeper reads for {t.title}</div>
            </div>
          </div>
          <span className="chip text-[10px]" style={{ color: t.color, borderColor: t.color + '55' }}>Toolkit</span>
        </Link>

        <div className="mt-6">
          <div className="flex items-center gap-2 mb-3">
            <GraduationCap className="w-4 h-4 text-white/60" />
            <h3 className="font-display text-[18px]">Lessons</h3>
            <span className="chip text-[10px] ml-1">{t.completed_lessons}/{t.total_lessons}</span>
          </div>
          <div className="space-y-2">
          {t.lessons.map((l, idx) => (
            <button key={l.id} onClick={() => setActiveLesson(l.id)} className={`w-full card-shell p-4 flex items-center gap-4 hover-lift text-left ${l.completed ? 'ring-1 ring-[#22d3ee]/40' : ''}`} data-testid={`rc-academy-lesson-${l.id}`}>
              <div className={`w-9 h-9 rounded-full grid place-items-center shrink-0 ${l.completed ? 'bg-[#22d3ee]/25 text-[#22d3ee]' : 'bg-white/5 text-white/60'}`}>
                {l.completed ? <Check className="w-5 h-5" /> : <span className="font-semibold">{idx + 1}</span>}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-[15px]">{l.title}</div>
                {l.completed && <div className="text-[12px] text-[#22d3ee] mt-0.5">Completed · {l.score}/3 quiz</div>}
              </div>
              <ChevronRight className="w-4 h-4 text-white/40" />
            </button>
          ))}
          </div>
        </div>
      </div>
    );
  }

  // Overview (track grid)
  return (
    <div data-testid="rc-academy-tab">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 text-[#ffcf52]"><GraduationCap className="w-4 h-4" /><span className="uppercase text-[11px] tracking-widest font-bold">Music Biz Academy</span></div>
          <h2 className="font-display text-[28px] mt-1">Know the business. Keep the money.</h2>
          <p className="text-white/60 text-[13.5px] mt-1 max-w-[720px]">{data.tracks.length} tracks, {data.tracks.reduce((s, t) => s + t.total_lessons, 0)} lessons, {data.specialists.length} specialist AI agents. Complete a track to unlock its specialist — your focused-domain coach.</p>
        </div>
        <div className="card-shell p-4 flex items-center gap-5" data-testid="rc-academy-xp">
          <div>
            <div className="text-[11px] uppercase tracking-wider text-white/45">XP</div>
            <div className="font-display text-[28px] brand-gradient">{data.xp.total}</div>
          </div>
          <div className="w-px h-10 bg-white/10" />
          <div>
            <div className="text-[11px] uppercase tracking-wider text-white/45">Tier</div>
            <div className="font-display text-[18px] capitalize">{data.user_tier}</div>
          </div>
        </div>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 mt-6">
        {data.tracks.map(t => {
          const Ic = TRACK_ICONS[t.icon] || GraduationCap;
          const pct = Math.round((t.completed_lessons / t.total_lessons) * 100);
          return (
            <button key={t.id} onClick={() => !t.locked && setActiveTrack(t.id)} disabled={t.locked} className={`card-shell p-5 text-left hover-lift disabled:cursor-not-allowed relative ${t.locked ? 'opacity-60' : ''}`} style={{ borderColor: t.color + '44' }} data-testid={`rc-academy-track-${t.id}`}>
              {t.locked && <div className="absolute top-3 right-3 chip flex items-center gap-1"><Lock className="w-3 h-3" /> {t.tier}</div>}
              {!t.locked && t.completed_lessons === t.total_lessons && <div className="absolute top-3 right-3 chip active flex items-center gap-1"><Award className="w-3 h-3" /> complete</div>}
              <div className="w-12 h-12 rounded-xl grid place-items-center mb-3" style={{ background: t.color + '22', color: t.color }}><Ic className="w-6 h-6" /></div>
              <div className="text-[11px] uppercase tracking-wider font-bold" style={{ color: t.color }}>{t.tier} · {t.total_lessons} lessons</div>
              <div className="font-display text-[20px] mt-1 leading-tight">{t.title}</div>
              <p className="text-white/65 text-[13px] mt-2 line-clamp-3">{t.summary}</p>
              <div className="mt-4">
                <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
                  <div className="h-full rounded-full transition-all" style={{ width: pct + '%', background: t.color }} />
                </div>
                <div className="text-[11.5px] text-white/50 mt-1.5 flex items-center justify-between">
                  <span>{t.completed_lessons}/{t.total_lessons} lessons</span>
                  <span className="flex items-center gap-1"><Sparkles className="w-3 h-3" /> unlocks {t.specialist_id.replace(/-/g, ' ')}</span>
                </div>
                {t.agency_count > 0 && (
                  <div className="text-[11px] text-[#a78bfa]/90 mt-1.5 flex items-center gap-1.5">
                    <ExternalLink className="w-3 h-3" /> {t.agencies_completed || 0}/{t.agency_count} real-world agency actions
                  </div>
                )}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function CertificateActions({ trackId }) {
  const [busy, setBusy] = useState(false);
  const download = async () => {
    setBusy(true);
    try {
      const url = await academyDownloadCertificate(trackId);
      const a = document.createElement('a');
      a.href = url; a.download = `vidcreforge-academy-${trackId}.png`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast.success('Certificate downloaded');
    } catch { toast.error('Failed to generate certificate'); }
    finally { setBusy(false); }
  };
  return (
    <div className="mt-3 flex items-center gap-2">
      <span className="chip active"><Award className="w-3.5 h-3.5" /> Certified</span>
      <button onClick={download} disabled={busy} className="btn-ghost text-[12.5px]" data-testid={`rc-academy-cert-download-${trackId}`}>
        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />} Download certificate
      </button>
    </div>
  );
}

function LessonReader({ trackId, lessonId, onBack, onCompleteTrack }) {
  const [data, setData] = useState(null);
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    academyLesson(trackId, lessonId).then(r => { setData(r); setAnswers({}); setResult(null); }).catch(() => toast.error('Failed to load lesson'));
  }, [trackId, lessonId]);

  if (!data) return <div className="flex items-center gap-2 text-white/60 py-10"><Loader2 className="w-5 h-5 animate-spin" /> Loading…</div>;

  const submit = async () => {
    if (Object.keys(answers).length !== data.lesson.quiz.length) return toast.error('Answer all questions');
    setSubmitting(true);
    try {
      const r = await academyComplete(trackId, lessonId, data.lesson.quiz.map((_, i) => answers[i]));
      setResult(r);
      if (r.passed) toast.success(`Passed ${r.score}/${r.total} — lesson complete`);
      else toast.error(`${r.score}/${r.total} — need ${r.total - 1}. Review and retry.`);
      if (r.specialist_unlocked) toast.success('🎉 New specialist agent unlocked!');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setSubmitting(false); }
  };

  return (
    <div data-testid="rc-academy-lesson-reader" className="max-w-[820px]">
      <button onClick={onBack} className="btn-ghost text-[13px] mb-4"><ArrowLeft className="w-4 h-4" /> Back to track</button>
      <div className="text-[11px] uppercase tracking-wider font-bold" style={{ color: data.track.color }}>{data.track.title}</div>
      <h2 className="font-display text-[28px] mt-1 leading-tight">{data.lesson.title}</h2>

      <article className="card-shell p-6 mt-5 text-white/85 text-[14.5px] leading-[1.75] whitespace-pre-wrap" data-testid="rc-academy-reading">
        {data.lesson.reading}
      </article>

      <div className="card-shell p-6 mt-5">
        <div className="flex items-center gap-2 text-[#ffcf52]"><Star className="w-4 h-4" /><span className="uppercase text-[11px] tracking-widest font-bold">Quick check — pass any 2 of 3</span></div>
        <div className="mt-4 space-y-5">
          {data.lesson.quiz.map((q, qi) => {
            const per = result?.per_question?.[qi];
            return (
              <div key={qi}>
                <div className="font-semibold text-[14.5px] flex items-start gap-2">
                  {per && (per.correct
                    ? <Check className="w-4 h-4 text-[#22d3ee] mt-0.5 shrink-0" />
                    : <X className="w-4 h-4 text-[#ff6b8a] mt-0.5 shrink-0" />)}
                  <span>{qi + 1}. {q.q}</span>
                </div>
                <div className="mt-2 space-y-1.5">
                  {q.options.map((opt, oi) => {
                    const sel = answers[qi] === oi;
                    const isCorrect = per && per.correct_index === oi;
                    const isYourWrong = per && per.your_answer === oi && !per.correct;
                    let cls = 'border-white/10 hover:border-white/25';
                    if (per) {
                      if (isCorrect) cls = 'border-[#22d3ee] bg-[#22d3ee]/15 text-[#22d3ee]';
                      else if (isYourWrong) cls = 'border-[#ff6b8a] bg-[#ff6b8a]/10 text-[#ff6b8a] line-through';
                      else cls = 'border-white/5 opacity-60';
                    } else if (sel) cls = 'border-[#22d3ee] bg-[#22d3ee]/10';
                    return (
                      <button
                        key={oi}
                        onClick={() => !result && setAnswers(a => ({ ...a, [qi]: oi }))}
                        disabled={!!result}
                        className={`w-full text-left px-3 py-2 rounded-lg border text-[13.5px] transition-all flex items-center gap-2 ${cls} ${result ? 'cursor-default' : 'cursor-pointer'}`}
                        data-testid={`rc-academy-q${qi}-opt${oi}`}
                      >
                        <span className="flex-1">{String.fromCharCode(97 + oi)}) {opt}</span>
                        {per && isCorrect && <Check className="w-4 h-4 shrink-0" />}
                        {per && isYourWrong && <X className="w-4 h-4 shrink-0" />}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
        {!result && (
          <button onClick={submit} disabled={submitting} className="btn-primary mt-5 disabled:opacity-50" data-testid="rc-academy-quiz-submit">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Submit quiz
          </button>
        )}
        {result && (
          <div className={`mt-5 card-shell p-4 ${result.passed ? 'border-[#22d3ee]/50 bg-[#22d3ee]/10' : 'border-[#ff6b8a]/50 bg-[#ff6b8a]/10'}`}>
            <div className="font-display text-[20px]">{result.passed ? '✅ Passed!' : '❌ Not quite'}</div>
            <div className="text-[13.5px] text-white/80 mt-1">{result.score} / {result.total} correct. Correct answers highlighted above in blue{result.passed ? '' : '; your wrong picks are struck through in pink'}.</div>
            {result.track_complete && <div className="mt-2 text-[13.5px]">🎉 You finished the track — your new specialist agent is unlocked in the Specialists tab.</div>}
            <div className="mt-3 flex flex-wrap items-center gap-2">
              {result.track_complete && <button onClick={onCompleteTrack} className="btn-primary text-[13px]" data-testid="rc-academy-track-done">See Specialists</button>}
              {!result.passed && (
                <button onClick={() => { setAnswers({}); setResult(null); }} className="btn-primary text-[13px]" data-testid="rc-academy-quiz-retry">Try again</button>
              )}
              <button onClick={onBack} className="btn-ghost text-[13px]">Back to track</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ===== Specialists (unlocked AI agents) =====
export function SpecialistsTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(null);

  const load = async () => { try { setData(await academyCurriculum()); } catch {} finally { setLoading(false); } };
  useEffect(() => { load(); }, []);

  if (loading) return <div className="flex items-center gap-2 text-white/60 py-10"><Loader2 className="w-5 h-5 animate-spin" /> Loading specialists…</div>;
  if (!data) return null;
  if (open) return <SpecialistChatView specialist={data.specialists.find(s => s.id === open)} onBack={() => setOpen(null)} />;

  return (
    <div data-testid="rc-specialists-tab">
      <div className="flex items-center gap-2 text-[#a78bfa]"><Gem className="w-4 h-4" /><span className="uppercase text-[11px] tracking-widest font-bold">Specialist agents</span></div>
      <h2 className="font-display text-[28px] mt-1">Focused AI coaches — unlocked by your learning.</h2>
      <p className="text-white/60 text-[13.5px] mt-1 max-w-[720px]">Each Academy track earns you a domain specialist — a Claude-powered AI trained for that exact topic. Complete the track, get the coach.</p>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 mt-6">
        {data.specialists.map(s => (
          <div key={s.id} className={`card-shell p-5 relative ${s.unlocked ? '' : 'opacity-70'}`} data-testid={`rc-specialist-${s.id}`}>
            {!s.unlocked && <div className="absolute top-3 right-3 chip flex items-center gap-1 text-[10px]"><Lock className="w-3 h-3" /> locked</div>}
            {s.unlocked && <div className="absolute top-3 right-3 chip active flex items-center gap-1 text-[10px]"><Check className="w-3 h-3" /> unlocked</div>}
            <div className="text-[32px] leading-none">{s.emoji}</div>
            <div className="font-display text-[20px] mt-3">{s.name}</div>
            <p className="text-white/65 text-[13px] mt-1.5 leading-relaxed min-h-[52px]">{s.blurb}</p>
            <button
              onClick={() => s.unlocked ? setOpen(s.id) : toast.error(`Complete the "${(data.tracks.find(t => t.specialist_id === s.id) || {}).title || 'linked track'}" track to unlock.`)}
              className={`${s.unlocked ? 'btn-primary' : 'btn-ghost'} w-full justify-center mt-4`}
              data-testid={`rc-specialist-open-${s.id}`}
            >
              {s.unlocked ? <><Bot className="w-4 h-4" /> Consult {s.name}</> : <><Lock className="w-4 h-4" /> Locked</>}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

function SpecialistChatView({ specialist, onBack }) {
  const [sessions, setSessions] = useState([]);
  const [sid, setSid] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  const loadSessions = async () => { try { setSessions(await specialistSessions(specialist.id)); } catch {} };
  useEffect(() => { loadSessions(); }, [specialist.id]);
  useEffect(() => {
    if (!sid) { setMessages([]); return; }
    specialistHistory(specialist.id, sid).then(r => setMessages(r.messages || [])).catch(() => {});
  }, [sid, specialist.id]);
  useEffect(() => { if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight; }, [messages, sending]);

  const send = async () => {
    const body = text.trim();
    if (!body || sending) return;
    setSending(true);
    const stub = { id: 'tmp-' + Date.now(), role: 'user', text: body, created_at: new Date().toISOString() };
    setMessages(m => [...m, stub]); setText('');
    try {
      const r = await specialistChat(specialist.id, { session_id: sid, text: body });
      setSid(r.session_id);
      setMessages(m => [...m.filter(x => x.id !== stub.id), r.user_message, r.assistant_message]);
      loadSessions();
    } catch (e) {
      setMessages(m => m.filter(x => x.id !== stub.id));
      toast.error(e?.response?.data?.detail?.message || e?.response?.data?.detail || 'Failed');
    } finally { setSending(false); }
  };

  return (
    <div data-testid={`rc-specialist-chat-${specialist.id}`}>
      <button onClick={onBack} className="btn-ghost text-[13px] mb-4"><ArrowLeft className="w-4 h-4" /> Back to specialists</button>
      <div className="grid md:grid-cols-[260px_1fr] gap-4 min-h-[560px]">
        <aside className="card-shell p-3 flex flex-col">
          <button onClick={() => { setSid(null); setMessages([]); }} className="btn-primary w-full justify-center"><Plus className="w-4 h-4" /> New chat</button>
          <div className="text-[11px] uppercase tracking-wider text-white/40 mt-3 px-1">Your sessions</div>
          <div className="flex-1 overflow-y-auto mt-2 space-y-1">
            {sessions.length === 0 && <div className="text-[12.5px] text-white/45 px-2 py-4">No chats yet.</div>}
            {sessions.map(s => (
              <div key={s.id} onClick={() => setSid(s.id)} className={`rounded-lg px-2 py-2 cursor-pointer hover:bg-white/5 ${sid === s.id ? 'bg-white/[0.07]' : ''}`}>
                <div className="text-[13px] truncate">{s.title || 'Session'}</div>
              </div>
            ))}
          </div>
        </aside>
        <section className="card-shell flex flex-col overflow-hidden">
          <header className="p-4 border-b border-white/5 flex items-center gap-3">
            <div className="w-10 h-10 rounded-full grid place-items-center text-[22px]" style={{ background: 'rgba(167,139,250,0.15)' }}>{specialist.emoji}</div>
            <div className="flex-1">
              <div className="font-display text-[17px] leading-tight">{specialist.name}</div>
              <div className="text-[11.5px] text-white/55">{specialist.blurb}</div>
            </div>
          </header>
          <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 md:p-6 space-y-4">
            {messages.length === 0 && !sending && (
              <div className="text-white/55 text-[13.5px]">Start a question about {specialist.name.toLowerCase().replace('agent', '').trim()}…</div>
            )}
            {messages.map(m => m.role === 'assistant'
              ? <AssistantBubble key={m.id} message={m} sourceKey={`specialist:${specialist.id}`} avatar={
                  <div className="w-8 h-8 rounded-full grid place-items-center shrink-0 text-[16px]" style={{ background: 'rgba(167,139,250,0.2)' }}>{specialist.emoji}</div>
                } />
              : <UserBubble key={m.id} message={m} />
            )}
            {sending && <div className="flex gap-3"><div className="w-8 h-8 rounded-full grid place-items-center text-[16px]" style={{ background: 'rgba(167,139,250,0.2)' }}>{specialist.emoji}</div><div className="bg-white/[0.04] border border-white/10 rounded-2xl px-4 py-2.5 text-[13.5px] text-white/60 flex items-center gap-2"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Thinking…</div></div>}
          </div>
          <form onSubmit={e => { e.preventDefault(); send(); }} className="p-3 border-t border-white/5 flex gap-2">
            <textarea rows={1} value={text} onChange={e => setText(e.target.value)} onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }} placeholder={`Ask ${specialist.name}…`} className="input-field flex-1 resize-none" disabled={sending} data-testid={`specialist-input-${specialist.id}`} />
            <button type="submit" disabled={!text.trim() || sending} className="btn-primary disabled:opacity-50 h-[42px]" data-testid={`specialist-send-${specialist.id}`}>{sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}</button>
          </form>
        </section>
      </div>
    </div>
  );
}

// ===== Old Academy (legacy short-article grid) — retained as fallback if needed =====
function LegacyAcademyTab() {
  const [items, setItems] = useState([]);
  const [cat, setCat] = useState('all');
  useEffect(() => { rcAcademy().then(setItems).catch(() => {}); }, []);
  const cats = useMemo(() => ['all', ...Array.from(new Set(items.map(i => i.category)))], [items]);
  const visible = cat === 'all' ? items : items.filter(i => i.category === cat);
  return (
    <div>
      <div className="flex items-center gap-2 text-[#ffcf52]"><GraduationCap className="w-4 h-4" /><span className="uppercase text-[11px] tracking-widest font-bold">Music Biz Academy</span></div>
      <h2 className="font-display text-[26px] mt-1">Educate now. Earn later.</h2>
      <div className="flex flex-wrap gap-2 mt-5">
        {cats.map(c => <button key={c} onClick={() => setCat(c)} className={`chip capitalize ${cat === c ? 'active' : ''}`}>{c}</button>)}
      </div>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 mt-5">
        {visible.map(a => (
          <div key={a.id} className="card-shell p-5 hover-lift">
            <div className="text-[11px] uppercase tracking-wider text-[#ffcf52]">{a.category}</div>
            <div className="font-display text-[18px] mt-1">{a.title}</div>
            <p className="text-white/70 text-[13px] mt-2">{a.summary}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

// ===== Plans =====
function PlansTab({ current, onChange }) {
  const [tiers, setTiers] = useState([]);
  const [busy, setBusy] = useState('');
  useEffect(() => { rcTiers().then(setTiers).catch(() => {}); }, []);

  const choose = async (tid) => {
    setBusy(tid);
    try { await rcSetTier(tid); toast.success('ReaperCast tier updated'); onChange?.(); }
    catch (err) { toast.error(err?.response?.data?.detail || 'Failed'); }
    finally { setBusy(''); }
  };

  return (
    <div data-testid="rc-plans-tab">
      <div className="flex items-center gap-2 text-[#a78bfa]"><Crown className="w-4 h-4" /><span className="uppercase text-[11px] tracking-widest font-bold">ReaperCast tiers</span></div>
      <h2 className="font-display text-[26px] mt-1">Add ReaperCast to your account</h2>
      <p className="text-white/60 text-[13.5px] mt-1">Track-limit scales with your tier. Add-on to your VidCreForge plan — they stack.</p>
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
        {tiers.map(t => {
          const on = current === t.id;
          return (
            <div key={t.id} className={`card-shell p-5 flex flex-col relative ${t.id === 'pro' ? 'ring-2 ring-[#a78bfa]/50' : ''}`} data-testid={`rc-tier-${t.id}`}>
              {t.id === 'pro' && <div className="absolute -top-3 left-5 chip active">Most popular</div>}
              {on && <div className="absolute -top-3 right-5 chip active">Active</div>}
              <div className="font-display text-[22px]">{t.name}</div>
              <div className="text-[12px] text-white/55">{t.badge}</div>
              <div className="mt-4 flex items-baseline gap-1">
                <span className="font-display text-[40px]">${t.price}</span>
                <span className="text-white/55 text-[12px]">/mo add-on</span>
              </div>
              <ul className="mt-3 space-y-1.5 text-[13px] text-white/80 flex-1">
                <li className="flex items-start gap-1.5"><Check className="w-3.5 h-3.5 text-[#22d3ee] mt-0.5 shrink-0" /> {t.track_limit === null ? 'Unlimited tracks' : `${t.track_limit} tracks`}</li>
                <li className="flex items-start gap-1.5"><Check className="w-3.5 h-3.5 text-[#22d3ee] mt-0.5 shrink-0" /> {t.radio_rotation ? 'Radio rotation included' : 'Upload only (no rotation)'}</li>
                <li className="flex items-start gap-1.5"><Check className="w-3.5 h-3.5 text-[#22d3ee] mt-0.5 shrink-0" /> Music Biz Academy</li>
                <li className="flex items-start gap-1.5"><Check className="w-3.5 h-3.5 text-[#22d3ee] mt-0.5 shrink-0" /> Distribution partner referrals</li>
              </ul>
              <button disabled={on || busy === t.id} onClick={() => choose(t.id)} className={`mt-4 w-full ${on ? 'btn-ghost opacity-60' : 'btn-primary'} disabled:opacity-50`} data-testid={`rc-choose-${t.id}`}>
                {busy === t.id ? <Loader2 className="w-4 h-4 animate-spin" /> : (on ? 'Active' : 'Activate')}
              </button>
            </div>
          );
        })}
      </div>
      <p className="text-[12px] text-white/45 mt-4">Pricing will wire to Stripe in the next release. For now this self-selects your tier for testing.</p>
    </div>
  );
}


// ===== AI Marketing Agent =====
const STARTER_PROMPTS = [
  'Review my catalog and tell me what genre lane I actually sit in.',
  'Plan a 4-week release strategy for my next single.',
  "I'm broke. What's the highest-leverage thing I can do this week to grow?",
  'Write me a 120-word artist bio for press & DSP profiles.',
  'Explain mechanical royalties vs. performance royalties like I\'m 12.',
];

function AgentTab() {
  const [sessions, setSessions] = useState([]);
  const [sid, setSid] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [loadingHist, setLoadingHist] = useState(false);
  const scrollRef = useRef(null);

  const loadSessions = async () => { try { setSessions(await rcAgentSessions()); } catch {} };
  useEffect(() => { loadSessions(); }, []);

  useEffect(() => {
    if (!sid) { setMessages([]); return; }
    setLoadingHist(true);
    rcAgentHistory(sid).then(r => setMessages(r.messages || [])).catch(() => toast.error('Failed to load chat')).finally(() => setLoadingHist(false));
  }, [sid]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, sending]);

  const send = async (override) => {
    const body = (override ?? text).trim();
    if (!body || sending) return;
    setSending(true);
    // Optimistic append
    const userStub = { id: 'tmp-u-' + Date.now(), role: 'user', text: body, created_at: new Date().toISOString() };
    setMessages(m => [...m, userStub]);
    setText('');
    try {
      const r = await rcAgentSend({ session_id: sid, text: body });
      setSid(r.session_id);
      setMessages(m => [...m.filter(x => x.id !== userStub.id), r.user_message, r.assistant_message]);
      loadSessions();
    } catch (e) {
      setMessages(m => m.filter(x => x.id !== userStub.id));
      toast.error(e?.response?.data?.detail || 'Agent unavailable');
    } finally { setSending(false); }
  };

  const newChat = () => { setSid(null); setMessages([]); setText(''); };
  const delSession = async (id) => {
    if (!window.confirm('Delete this chat?')) return;
    try { await rcAgentDelete(id); if (sid === id) newChat(); loadSessions(); } catch { toast.error('Failed'); }
  };

  return (
    <div data-testid="rc-agent-tab" className="grid md:grid-cols-[260px_1fr] gap-4 min-h-[560px]">
      <aside className="card-shell p-3 flex flex-col">
        <button onClick={newChat} className="btn-primary w-full justify-center" data-testid="rc-agent-new">
          <Plus className="w-4 h-4" /> New chat
        </button>
        <div className="mt-3 text-[11px] uppercase tracking-wider text-white/40 px-1">Recent</div>
        <div className="flex-1 overflow-y-auto mt-2 space-y-1">
          {sessions.length === 0 && <div className="text-[12.5px] text-white/45 px-2 py-4">No chats yet. Fire off a question →</div>}
          {sessions.map(s => (
            <div key={s.id} className={`group flex items-center gap-1 rounded-lg px-2 py-2 cursor-pointer hover:bg-white/5 ${sid === s.id ? 'bg-white/[0.07] ring-1 ring-[#a78bfa]/30' : ''}`} onClick={() => setSid(s.id)} data-testid={`rc-agent-session-${s.id}`}>
              <MessageSquare className="w-3.5 h-3.5 text-white/50 shrink-0" />
              <div className="text-[13px] truncate flex-1">{s.title || 'Chat'}</div>
              <button onClick={(e) => { e.stopPropagation(); delSession(s.id); }} className="opacity-0 group-hover:opacity-100 text-white/50 hover:text-[#ff6b8a]" aria-label="Delete chat"><Trash2 className="w-3.5 h-3.5" /></button>
            </div>
          ))}
        </div>
        <div className="text-[11px] text-white/40 mt-3 px-1 leading-relaxed">ReaperBot uses your catalog + tier as context. It doesn't share your data with other artists.</div>
      </aside>

      <section className="card-shell flex flex-col overflow-hidden">
        <header className="p-4 border-b border-white/5 flex items-center gap-3">
          <div className="w-9 h-9 rounded-full grid place-items-center" style={{ background: 'linear-gradient(135deg,#a78bfa,#22d3ee)' }}>
            <Bot className="w-4.5 h-4.5 text-black" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-display text-[17px] leading-tight">ReaperBot</div>
            <div className="text-[11.5px] text-white/55">Your in-house music business & marketing agent</div>
          </div>
          <div className="chip active hidden sm:inline-flex">Beta</div>
        </header>

        <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 md:p-6 space-y-4" data-testid="rc-agent-thread">
          {loadingHist && <div className="flex items-center gap-2 text-white/55 text-[13px]"><Loader2 className="w-4 h-4 animate-spin" /> Loading history…</div>}
          {!loadingHist && messages.length === 0 && (
            <div className="max-w-[620px]">
              <h3 className="font-display text-[24px] leading-tight">What do you want to move on this week?</h3>
              <p className="text-white/60 text-[13.5px] mt-1">Ask about releases, distribution, royalties, press, playlist pitching — or pick a starter.</p>
              <div className="grid sm:grid-cols-2 gap-2 mt-4">
                {STARTER_PROMPTS.map((p, i) => (
                  <button key={i} onClick={() => send(p)} className="text-left card-shell p-3 hover-lift text-[13px] leading-snug" data-testid={`rc-agent-starter-${i}`}>{p}</button>
                ))}
              </div>
            </div>
          )}
          {messages.map(m => m.role === 'assistant'
            ? <AssistantBubble key={m.id} message={m} sourceKey="reaper-agent" avatar={
                <div className="w-8 h-8 rounded-full grid place-items-center shrink-0" style={{ background: 'linear-gradient(135deg,#a78bfa,#22d3ee)' }}>
                  <Bot className="w-4 h-4 text-black" />
                </div>
              } />
            : <UserBubble key={m.id} message={m} />
          )}
          {sending && (
            <div className="flex gap-3">
              <div className="w-8 h-8 rounded-full grid place-items-center shrink-0" style={{ background: 'linear-gradient(135deg,#a78bfa,#22d3ee)' }}><Bot className="w-4 h-4 text-black" /></div>
              <div className="bg-white/[0.04] border border-white/10 rounded-2xl px-4 py-2.5 text-[13.5px] text-white/60 flex items-center gap-2"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Thinking…</div>
            </div>
          )}
        </div>

        <form onSubmit={(e) => { e.preventDefault(); send(); }} className="p-3 border-t border-white/5 flex gap-2 items-end">
          <textarea
            rows={1}
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
            placeholder="Ask ReaperBot anything — releases, royalties, press, pitching…"
            className="input-field flex-1 resize-none max-h-40"
            data-testid="rc-agent-input"
            disabled={sending}
          />
          <button type="submit" disabled={!text.trim() || sending} className="btn-primary disabled:opacity-50 h-[42px]" data-testid="rc-agent-send">
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          </button>
        </form>
      </section>
    </div>
  );
}
